import type { ExportFit } from '../schemas/export.js';
import type { ExportModel, ExportModelColumn } from './build-export-model.js';
import type { PageGeometry } from './page-geometry.js';
import {
  DEFAULT_COLUMN_WIDTH_PX,
  DEFAULT_ROW_HEIGHT_PX,
  ROW_LABEL_WIDTH_PX,
} from './page-geometry.js';

export interface TilePage {
  /** Indices into `ExportModel.columns` rendered on this tile. */
  columnIndices: number[];
  /** Indices into `ExportModel.rows` rendered on this tile. */
  rowIndices: number[];
  /** This tile's position in the overall page grid — 0-based, left-to-right / top-to-bottom. */
  columnPageIndex: number;
  rowPageIndex: number;
  /** The single uniform scale (as a whole percent) applied to every tile. */
  scalePercent: number;
}

function scaleForAxis(axis: 'auto' | number, totalSize: number, usableSize: number): number {
  if (axis === 'auto' || totalSize === 0) return 1;
  return Math.min(1, (axis * usableSize) / totalSize);
}

/**
 * A fit scale is `capacity / total`, so the scaled sizes sum back to the
 * capacity only up to floating-point error — and `Σ(size·scale)` can land a
 * hair *above* it, which spilled the last row of a « tout sur une page »
 * export onto a second page. A billionth of a pixel absorbs that noise without
 * letting any real overflow through.
 */
const PACKING_TOLERANCE_PX = 1e-9;

/**
 * Greedy bin-packing: fills each page in order, never reordering items,
 * starting a new page only on overflow — or, when the incoming index is in
 * `forcedBreaksBefore` and the current page already has content, on that
 * forced break (spec R2's `pageBreaksBefore`). The column axis passes no
 * forced breaks.
 */
function packIntoPages(
  sizes: number[],
  scale: number,
  pageCapacity: number,
  forcedBreaksBefore?: ReadonlySet<number>,
): number[][] {
  const pages: number[][] = [];
  let current: number[] = [];
  let currentTotal = 0;
  sizes.forEach((size, index) => {
    const scaledSize = size * scale;
    const forcedBreak = current.length > 0 && (forcedBreaksBefore?.has(index) ?? false);
    const overflows = currentTotal + scaledSize > pageCapacity + PACKING_TOLERANCE_PX;
    if (forcedBreak || (current.length > 0 && overflows)) {
      pages.push(current);
      current = [];
      currentTotal = 0;
    }
    current.push(index);
    currentTotal += scaledSize;
  });
  pages.push(current);
  return pages;
}

/**
 * The axis constraints the bin-packing math consumes — an *internal*
 * representation only. "Pages wide × pages tall" describes the mechanism
 * rather than the intent (and its `auto`/`auto` case silently means "never
 * shrink"), so it is not on the wire and not user-facing: `ExportFit` names
 * the outcome and `constraintForFit` is the single place one becomes the
 * other. There is deliberately no `scalePercent` arm — free-form scaling is
 * retired from the wire contract (spec R1).
 */
type FitConstraint = { pagesWide: 'auto' | number; pagesTall: 'auto' | number };

/** Spec R1 — the one place a named fit intent becomes tiling-math constraints. */
export function constraintForFit(fit: ExportFit): FitConstraint {
  switch (fit) {
    case 'aucun':
      return { pagesWide: 'auto', pagesTall: 'auto' };
    case 'une-page-de-large':
      return { pagesWide: 1, pagesTall: 'auto' };
    case 'une-page-de-haut':
      return { pagesWide: 'auto', pagesTall: 1 };
    case 'une-seule-page':
      return { pagesWide: 1, pagesTall: 1 };
  }
}

/** The usable content box of one page, driven entirely by `PageGeometry` — no hardcoded page size or margin. */
function usablePageSize(
  geometry: PageGeometry,
  reservedHeaderHeightPx: number,
  reservedFooterHeightPx: number,
  labelColumnCount: 1 | 2,
): { width: number; height: number } {
  return {
    width: geometry.pageWidthPx - 2 * geometry.marginPx - labelColumnCount * ROW_LABEL_WIDTH_PX,
    height:
      geometry.pageHeightPx -
      2 * geometry.marginPx -
      reservedHeaderHeightPx -
      reservedFooterHeightPx,
  };
}

/**
 * Resolves `pageBreaksBefore` (row keys, spec R2) to the row *indices*
 * `packIntoPages` needs. A break key `k` matches row index `i` when
 * `rowKeys[i]` ends with `:${k}` — `rowKeys` entries are `${kind}:${key}`, so
 * this matches on the bare key regardless of `kind`.
 */
function resolveForcedRowBreaks(
  pageBreaksBefore: readonly string[] | undefined,
  rowKeys: readonly string[],
): ReadonlySet<number> {
  if (!pageBreaksBefore || pageBreaksBefore.length === 0) return new Set();
  const indices = new Set<number>();
  rowKeys.forEach((rowKey, index) => {
    if (pageBreaksBefore.some((breakKey) => rowKey.endsWith(`:${breakKey}`))) {
      indices.add(index);
    }
  });
  return indices;
}

/**
 * Splits the model into the tiles that will be rendered, one per physical
 * page, and resolves the single uniform scale they all share (export-rework
 * chantier 1, spec R1-R2).
 *
 * `geometry` carries the page size and margins, `fit` names the scaling
 * intent, and `pageBreaksBefore`/`rowKeys` add user-forced row-page breaks.
 */
export function computeTilePages(input: {
  model: ExportModel;
  columnWidths: Record<string, number>;
  rowHeights: Record<string, number>;
  geometry: PageGeometry;
  fit: ExportFit;
  /**
   * How many fixed-width row-label columns the rendered tile carries — 1
   * normally, 2 when `activationLevierNames` is `column` and each activation
   * row gets its own levier-name cell beside the spanning group label. Each
   * one costs `ROW_LABEL_WIDTH_PX` of horizontal page budget, so the
   * bin-packing has to reserve them or the second label column would push the
   * rightmost palier column off the page.
   */
  labelColumnCount: 1 | 2;
  /**
   * Vertical space to reserve on every page for overhead that shares physical
   * page space with the tiled grid but isn't part of the row-height budget —
   * the per-tile column-header row and/or the document's own header and
   * optional subject/description block. Deliberately a single uniform-per-page
   * value (not position-aware per tile): it slightly over-reserves height on
   * pages that don't actually carry the full overhead, in exchange for
   * guaranteeing no page ever silently overflows.
   */
  reservedHeaderHeightPx: number;
  /**
   * Vertical space the document footer takes at the bottom of every page —
   * `FOOTER_RESERVED_PX` when `documentFooterEnabled`, else 0 (the default).
   * `planPdfDocument` is the caller that passes it; subtracting it from the
   * usable height also makes a fit's scale (`une-seule-page`, …) account for
   * the footer instead of letting it overlap the last row.
   */
  reservedFooterHeightPx?: number;
  /** Row keys (levier order or dimension UUID) that force a new row-page before them (spec R2). */
  pageBreaksBefore?: readonly string[];
  /** `${kind}:${key}` per `model.rows` entry, in the same order — used to match `pageBreaksBefore` against row indices. */
  rowKeys: string[];
}): TilePage[] {
  const usable = usablePageSize(
    input.geometry,
    input.reservedHeaderHeightPx,
    input.reservedFooterHeightPx ?? 0,
    input.labelColumnCount,
  );

  const columnSizes = input.model.columns.map(
    (column) => input.columnWidths[String(column.number)] ?? DEFAULT_COLUMN_WIDTH_PX,
  );
  const rowSizes = input.model.rows.map(
    (row) => input.rowHeights[`${row.kind}:${row.key}`] ?? DEFAULT_ROW_HEIGHT_PX,
  );
  const totalWidth = columnSizes.reduce((sum, size) => sum + size, 0);
  const totalHeight = rowSizes.reduce((sum, size) => sum + size, 0);

  const constraint = constraintForFit(input.fit);
  const scale = Math.min(
    scaleForAxis(constraint.pagesWide, totalWidth, usable.width),
    scaleForAxis(constraint.pagesTall, totalHeight, usable.height),
  );

  const forcedBreaksBefore = resolveForcedRowBreaks(input.pageBreaksBefore, input.rowKeys);

  const columnPages = packIntoPages(columnSizes, scale, usable.width);
  const rowPages = packIntoPages(rowSizes, scale, usable.height, forcedBreaksBefore);
  const scalePercent = Math.round(scale * 100);

  const tiles: TilePage[] = [];
  rowPages.forEach((rowIndices, rowPageIndex) => {
    columnPages.forEach((columnIndices, columnPageIndex) => {
      tiles.push({ columnIndices, rowIndices, columnPageIndex, rowPageIndex, scalePercent });
    });
  });
  return tiles;
}

/**
 * Pure reorder of `model.columns` to follow `columnOrder` (palier numbers, in
 * display order — spec R2's `ExportTableOptions.columnOrder`). Numbers absent
 * from `columnOrder` keep their relative natural (original) position at the
 * end; an empty array leaves the model unchanged (identity, not a copy).
 */
export function applyColumnOrder(model: ExportModel, columnOrder: readonly number[]): ExportModel {
  if (columnOrder.length === 0) return model;

  const remaining = new Map(model.columns.map((column) => [column.number, column] as const));
  const ordered: ExportModelColumn[] = [];
  for (const number of columnOrder) {
    const column = remaining.get(number);
    if (column) {
      ordered.push(column);
      remaining.delete(number);
    }
  }
  for (const column of model.columns) {
    if (remaining.has(column.number)) {
      ordered.push(column);
    }
  }

  return { ...model, columns: ordered };
}

/**
 * Whether the `repeatHeaders` option can change anything for these tiles —
 * decision L6 (2026-08-25 doc).
 *
 * `renderExportHtml` derives `showColumnHeaders = repeatHeaders ||
 * tile.rowPageIndex === 0` and `showRowLabels = repeatHeaders ||
 * tile.columnPageIndex === 0`, so the flag can only alter a tile that is past
 * the first row-page or the first column-page. A single-tile export has
 * neither, making the option provably inert — which is exactly the state the
 * UI must surface rather than leaving the user to toggle a dead checkbox.
 *
 * `tiles.length` is the product of the two page-axis counts, so `> 1` is
 * equivalent to "at least one axis paginated" and needs no index inspection.
 */
export function isRepeatHeadersEffective(tiles: TilePage[]): boolean {
  return tiles.length > 1;
}
